// ═══ HELP CONTENT — Single source of truth for tooltips + Help Page ═══
// Each entry has: short (tooltip), full (help page), formula/example (optional)
// Sections map to Help Page navigation

export const HELP_SECTIONS = [
  { id: "overview", label: "System Overview", icon: "📊" },
  { id: "scoring", label: "Risk Scoring", icon: "🎯" },
  { id: "groups", label: "Client Groups", icon: "👥" },
  { id: "flags", label: "Flags & Abuse Detection", icon: "🚩" },
  { id: "financial", label: "Financial Metrics", icon: "💰" },
  { id: "speed", label: "Speed Profiling", icon: "⚡" },
  { id: "arbitrage", label: "Arbitrage Detection", icon: "🔍" },
  { id: "ftd", label: "FTD Analysis", icon: "📈" },
  { id: "covering", label: "Covering Gold", icon: "🥇" },
  { id: "pages", label: "Pages & Navigation", icon: "📑" },
  { id: "glossary", label: "Glossary", icon: "📖" },
  { id: "fields", label: "Client Profile Fields", icon: "📋" },
];

export const HELP = {
  // ═══════════════════════════════════════════
  // SYSTEM OVERVIEW
  // ═══════════════════════════════════════════
  dataFlow: {
    section: "overview",
    title: "How Data Flows",
    short: "Data flows from MT5 trading platform → SQL Server (13 stored procedures) → Excel export → This app. Data is uploaded monthly per company.",
    full: `The system processes trading data through a 4-layer pipeline:

1. **MT5 Platform** — Raw trading activity (every trade, position, deal) is recorded in real-time by the MetaTrader 5 platform for both Al Salam Investments and River Prime.

2. **SQL Server** — 13 stored procedures and views aggregate raw MT5 data into meaningful metrics: speed analysis, hedge detection, Friday trading patterns, IP analysis, commission calculations, and more. Each procedure handles a specific risk dimension.

3. **Excel Export** — SQL output is exported as "Final Risk Result.xlsx" (one row per client per month, 104 columns covering all risk metrics) and "Final_Sales_Result.xlsx" for the CRM system.

4. **This Application** — Reads the Excel file, computes risk scores using 113 factors, assigns groups using configurable rules, and presents everything through interactive dashboards.

Data is uploaded per company (Al Salam and River Prime separately). Each upload contains multiple months of data. The app always shows the latest available month per company as the default view.`,
  },
  companies: {
    section: "overview",
    title: "Al Salam vs River Prime",
    short: "Two separate forex brokerages. Each has its own data file. The sidebar filter lets you view one company or both together.",
    full: `The system manages two separate trading companies:

• **Al Salam Investments** — Primary brokerage
• **River Prime** — Secondary brokerage

Each company uploads its own Excel data file independently. They may have different data months available — for example, Al Salam might have data through January while River Prime has data through February.

When you select "All" in the sidebar company filter, the system shows clients from both companies using each company's own latest month. This ensures no company's data is missing even if they have different reporting periods.

Each client is identified by the combination of Company + Login number, since the same Login number could exist in both companies.`,
  },
  monthFilter: {
    section: "overview",
    title: "Year & Month Filters",
    short: "Filter which months of data to view. Financial cards sum across selected months. Client list shows each client's latest month data.",
    full: `The top bar shows year buttons and month pills for filtering:

• **All (default)** — Shows data from the latest available month per company. Financial totals reflect just that month.
• **Specific year** — Shows only months from that year.
• **Specific months** — Click one or more month pills to filter. When multiple months are selected, financial metrics (Volume, Deposit, etc.) are SUMMED across all selected months, while client status and risk data comes from the latest selected month.

Example: If you select Jan + Feb 2026, a client's Volume will be Jan + Feb combined, but their Risk Score and Group come from their Feb data (the latest month).`,
  },
  cuAccount: {
    section: "overview",
    title: "CU (Client Unit) vs Login",
    short: "CU groups multiple trading accounts under one client. Total Clients (CU) counts unique clients, not individual accounts.",
    full: `A single client can have multiple trading accounts (Logins). The CU (Client Unit) number groups all accounts belonging to the same person.

• **Login** — Individual trading account number (e.g., 12003, 100201)
• **CU Account** — The client's unique identifier that groups their accounts

The Dashboard's "Total Clients (CU)" card counts unique CU numbers, not Logins. This gives the true client count. For example, if client John has Logins 12003, 12004, and 12005, they count as 1 CU but 3 Logins.

In Client Profiles and other tables, each row is one Login (account), and you may see the same client name appearing multiple times with different Login numbers.`,
  },

  // ═══════════════════════════════════════════
  // RISK SCORING
  // ═══════════════════════════════════════════
  riskScore: {
    section: "scoring",
    title: "Broker Risk Score",
    short: "A 0-100 score measuring how much risk a client poses to the broker. Calculated from 113 factors across 4 categories: Compliance (35), Revenue (30), Strategy (25), Position (10).",
    full: `The Broker Risk Score is the core metric of the system. It quantifies how much financial risk a client poses to the brokerage — NOT the client's personal risk.

A higher score means MORE risk to the broker (the client is more likely to cost the broker money through profitable trading, abuse patterns, or regulatory concerns).`,
    formula: `Total Score = Compliance (max 35) + Revenue (max 30) + Strategy (max 25) + Position (max 10)

Each category contains multiple factors. Within each group of related factors (e.g., ProfitFactor thresholds), only the HIGHEST matching factor scores points — they don't stack. But different groups DO stack.`,
    example: `Client #100201 example:
• Revenue: ProfitFactor=3.2 (scores 8pts from ≥2.5), Profit=$15K (scores 8pts from >$5K), WinRate=72% (scores 3pts from ≥65%), BestTrade=$8K (scores 4pts from >$5K) → Revenue total: 23/30
• Compliance: Speed<30s=45% (scores 3pts from >30%), Scalper flag (3pts), SpeedConsistency=0.12 (3pts from <0.15) → Compliance total: 9/35
• Strategy: HeavyTrader flag (3pts), DailyVolume=8 lots (2pts from >5), HedgeRatio=0.6 (2pts from >0.5) → Strategy total: 7/25  
• Position: Equity=$50K (1.5pts from >$20K), EquityRisk=High (2pts) → Position total: 3.5/10

TOTAL = 23 + 9 + 7 + 3.5 = 42.5 → Risk Level: HIGH (39-50)`,
  },
  riskLevels: {
    section: "scoring",
    title: "Risk Levels (Safe → Critical)",
    short: "5 risk levels based on score: Safe (0-15), Low (16-26), Elevated (27-38), High (39-50), Critical (51+).",
    full: `Risk scores map to 5 levels with color coding:

• **Safe (0-15)** 🟢 — Minimal risk. Typically inactive or very small traders. No action needed. These are the broker's profit center (B-Book candidates).

• **Low (16-26)** 🔵 — Some risk signals but manageable. May have moderate profits or a few flags. Monitor monthly.

• **Elevated (27-38)** 🟡 — Significant risk signals. Multiple flags or consistently profitable. Should be monitored weekly. Consider hybrid A/B-Book execution.

• **High (39-50)** 🟠 — Serious risk. Very profitable, multiple abuse patterns, or speed/arbitrage concerns. Requires active management — A-Book execution, latency controls, leverage reduction.

• **Critical (51+)** 🔴 — Maximum risk. Extreme profitability combined with abuse patterns. Full restriction recommended — A-Book only, maximum latency, slippage, daily monitoring, potential account review/closure.

These boundaries are configurable in Admin → Risk Settings.`,
  },
  revenueRisk: {
    section: "scoring",
    title: "Revenue Risk Category (30 pts)",
    short: "Measures how much money the client extracts from the broker. Includes ProfitFactor, net Profit, WinRate, BestTrade, GrossProfit, AvgProfitTrade, and Payoff Ratio.",
    full: `Revenue Risk (max 30 points) measures the client's profit extraction capability — how effectively they take money from the broker.

**Key factors scored:**
• **ProfitFactor** — Ratio of gross profit to gross loss. PF ≥ 2.5 is extremely profitable (8pts). PF ≥ 1.0 means they're making money overall.
• **Net Profit** — Total P&L. Scores from >$0 (1pt) up to >$5K (8pts).
• **Win Rate (ProfitPct)** — Percentage of winning trades. 75%+ is very high (5pts).
• **BestTrade** — Largest single winning trade. Large wins signal skill or exploitation.
• **GrossProfit** — Total of all winning trades (before subtracting losses).
• **AvgProfitTrade** — Average size of winning trades.
• **Payoff Ratio** — AvgProfitTrade ÷ |AvgLossTrade|. High ratio means wins are much bigger than losses — a hallmark of skilled or exploitative trading.

Within each metric (e.g., ProfitFactor), only the highest matching tier scores. A client with PF=3.0 gets 8pts (from ≥2.5), not 8+6+4+2.`,
  },
  complianceRisk: {
    section: "scoring",
    title: "Compliance Risk Category (35 pts)",
    short: "Detects rule-breaking behavior: speed abuse (3 layers), bot patterns, Friday gap trading, swap abuse, IP sharing, commission exploitation, and news trading.",
    full: `Compliance Risk (max 35 points) is the largest category because abuse patterns are the most actionable risk factor for brokers.

**Speed Abuse (3 layers):**
• Layer 1 — % of trades under 30 seconds. Extreme scalping.
• Layer 2 — % of trades under 2 minutes. Fast trading.
• Layer 3 — % of trades under 5 minutes. Broader speed signal.
Each layer scores independently (they stack).

**Bot/Arbitrage Detection:**
• SpeedConsistency — How uniform trade durations are. Below 0.15 = robotic (every trade takes almost exactly the same time).
• FastTradeWinRate — Win rate on trades closed within 2 minutes. If someone wins >85% of fast trades, likely using an automated system.
• MaxConsecutiveWins — Long winning streaks (25+) suggest algorithms.

**Friday Abuse:**
• FridayLateOpens — Opening trades late Friday to exploit the weekend gap.
• FridayHedgeBreaks — Opening hedges Friday then closing one side, creating directional exposure over the weekend.

**Other Abuse:**
• Swap Abuse — Exploiting swap-free accounts. Measured by |SwapInOut| amount.
• IP Abuse — IP_ODT = number of other trading accounts sharing the client's IP. Suggests coordinated trading.
• Commission Exploitation — CommissionRebateExploitation amount.
• News Trading — TradesNearNews10Min count.`,
  },
  strategyRisk: {
    section: "scoring",
    title: "Strategy Risk Category (25 pts)",
    short: "Measures trading strategy intensity: volume, frequency, trade size, duration, directional bias, hedge behavior, and instruments traded.",
    full: `Strategy Risk (max 25 points) evaluates how the client trades — their methods, intensity, and instruments.

**Volume & Frequency:**
• DailyVolumeLots — Average daily volume. >12 lots/day is heavy (3pts).
• TradingFreqPerDay — Trades per day. >80/day is extreme (3pts).
• MostUsedTradeSize — Typical position size in lots.

**Duration:**
• AvgTradeDuration — Average time a trade is open. <5 minutes = aggressive short-term trading.

**Directional Bias:**
• LongPct Extreme — How one-sided their trading is. If >95% of trades are all long (or all short), it's unusual (3pts). Calculated as: how far from 50% their long percentage is.

**Hedge Behavior:**
• HedgeRatio — Proportion of hedged volume to total volume. >0.7 = heavily hedged.
• HedgeVolumeLots — Absolute hedge volume.
• AvgHedgeDurationSec — How long hedges are held. Short hedges (<5 min) are more abusive.

**Instruments:**
• METALS — Trading gold/silver (volatile, popular for abuse).
• INDICES — Trading indices (another volatile instrument class).

**Flags:** HeavyTrader, HighFrequencyTrader, Hedger, LongTrader each contribute points and are grouped with their related numeric factors.`,
  },
  positionRisk: {
    section: "scoring",
    title: "Position Risk Category (10 pts)",
    short: "Measures current exposure: Equity level, Equity Risk classification, Equity Growth rate, FloatingPL, and open positions/volume.",
    full: `Position Risk (max 10 points) measures the client's current financial exposure — how much money is at stake right now.

**Equity:**
• Equity amount — >$100K (2pts), >$20K (1.5pts), >$5K (1pt).
• EquityRisk classification — "High" (2pts) or "Moderate" (1pt). Based on equity size relative to trading patterns.
• Equity Growth — Percentage change from previous month. >50% growth (2pts) signals rapid accumulation.

**Floating P&L:**
• FloatingPL — Unrealized profit/loss on open positions. Large floating profit means the client could close and extract significant money.

**Open Positions:**
• StillOpenPositions — Number of currently open trades. >20 is significant (1pt).
• StillOpenVolumeLots — Total volume of open positions. >5 lots is significant (1pt).

This category has the lowest weight (10 pts) because position risk is a snapshot in time and changes rapidly, while behavioral patterns in the other categories are more stable and predictive.`,
  },
  behavioralRisk: {
    section: "scoring",
    title: "Behavioral Risk (B.Risk)",
    short: "Binary classification: Normal or High. Based on trading behavior flags. 'High' means the client has patterns that are dangerous to the broker.",
    full: `Behavioral Risk (B.Risk) is a simplified binary classification derived from the client's trading behavior flags:

• **Normal** — No significant abuse patterns detected. Client trades within normal parameters.
• **High** — One or more concerning behavioral patterns detected (speed abuse, hedging abuse, commission exploitation, news trading, etc.).

This is different from the Broker Risk Score which is granular (0-100). B.Risk is a quick visual indicator — think of it as a "red flag or not" signal.

A client can have B.Risk = Normal but still have an elevated Risk Score if they're very profitable (revenue risk) without breaking any behavioral rules.`,
  },
  equityRisk: {
    section: "scoring",
    title: "Equity Risk (E.Risk)",
    short: "Measures financial exposure: Low, Moderate, or High. Based on the client's equity size, growth rate, and open position exposure.",
    full: `Equity Risk (E.Risk) is a 3-level classification focused on the client's financial size and exposure:

• **Low** — Small equity, minimal open positions. Limited financial impact if they profit.
• **Moderate** — Medium equity or growing. Worth monitoring but not urgent.
• **High** — Large equity, significant open positions, or rapid equity growth. This client has the capital to cause substantial broker losses.

E.Risk is independent of B.Risk. A client can be:
• B.Risk=Normal + E.Risk=High → Large client who trades safely (a VIP — treat well)
• B.Risk=High + E.Risk=Low → Small client with bad behavior (annoying but limited impact)  
• B.Risk=High + E.Risk=High → Large client with bad behavior (highest priority to manage)`,
  },
  scoringGroups: {
    section: "scoring",
    title: "How Factor Groups Work",
    short: "Related factors are grouped (e.g., ProfitFactor tiers). Within a group, only the HIGHEST matching tier scores. Different groups stack independently.",
    full: `The 113 scoring factors are organized into groups of related metrics. This prevents double-counting while still rewarding breadth of risk signals.

**Within a group** — Only the highest matching tier scores points.
Example: ProfitFactor group has tiers at ≥2.5 (8pts), ≥1.5 (6pts), ≥1.0 (4pts), ≥0.7 (2pts). A client with PF=2.0 matches ≥1.5 and ≥1.0 and ≥0.7, but only gets 6pts (the highest match).

**Across groups** — Points stack. A client scores from ProfitFactor AND Profit AND WinRate groups independently.

Example: Revenue category has 7 groups (ProfitFactor, Profit, WinRate, BestTrade, GrossProfit, AvgProfitTrade, Payoff Ratio). The theoretical maximum of 30 is hard to reach because you'd need extreme values in ALL 7 groups simultaneously.

This design means:
• A client who's profitable in ONE way gets a moderate score
• A client who's profitable in MANY ways gets a high score  
• A client who's extremely profitable in just one metric doesn't unfairly dominate`,
  },

  // ═══════════════════════════════════════════
  // CLIENT GROUPS
  // ═══════════════════════════════════════════
  groupSystem: {
    section: "groups",
    title: "Group Assignment System",
    short: "Clients are assigned to groups (B2B_A, HDG_A, CRT_A, SPD_A, PRO_A, STD_AB, STD_B) based on priority rules. First matching group wins.",
    full: `The Group Assignment Engine classifies every client into exactly one group. Groups determine the broker's execution strategy (A-Book vs B-Book), risk controls, and monitoring frequency.

**How it works:**
1. Groups are checked in priority order (top to bottom)
2. Each group has rules: client type matching and/or conditions
3. The FIRST group whose rules match wins — the client is assigned there
4. The last group (STD_B) has a "default" condition that catches everyone not matched above

**Rule types:**
• **Client Type Match** — Checks the ClientType field (e.g., "b2b", "hedger")
• **minScore** — Risk score above a threshold
• **arbitrage** — Client flagged as arbitrage trader  
• **minAbuseFlags** — Number of abuse flags above a threshold
• **profitable** — Revenue score above configured threshold
• **clean** — Compliance score below configured threshold
• **speedFlag** — Has Scalper, HFT, or NewsTrader flag
• **default** — Matches everyone

Groups, their rules, and priority order are all configurable in Admin → Group Settings.`,
  },
  groupB2B: {
    section: "groups",
    title: "B2B_A — Business A-Book",
    short: "Business-to-business clients. Matched by ClientType='b2b'. Full A-Book execution with revenue share.",
    full: `**B2B_A (Business A-Book)** — Highest priority group.

**Who:** Business-to-business clients, typically institutional or corporate accounts managed by partner firms.

**How matched:** ClientType = "b2b"

**Execution:** Full A-Book — all trades are hedged/passed to liquidity providers. Revenue is shared with the LP partner.

**Actions:** Tags Bridge, 5-Day Swap Free, Dynamic margin, NOP limit, Latency 150ms, Custom reporting.

**Why A-Book:** These are contractual relationships where B-Book would violate the partnership agreement. Revenue comes from volume and spread, not from client losses.`,
  },
  groupHDG: {
    section: "groups",
    title: "HDG_A — Hedger A-Book",
    short: "Physical or currency hedgers. Matched by ClientType='hedger' or 'pch_a'. A-Book with high spreads and low leverage.",
    full: `**HDG_A (Hedger A-Book)** — Second priority.

**Who:** Clients who are hedging real business exposure — importing/exporting companies, gold dealers, physical commodity traders.

**How matched:** ClientType = "hedger" or "pch_a" (physical commodity hedger A)

**Execution:** Full A-Book with defensive controls.

**Actions:** Swap Free (they need it for real business), High spread & commissions (they'll pay because it's a business cost), Low leverage (1:50), NOP limit, Admin fee, Monitor weekly.

**Why this treatment:** These clients have genuine commercial need. They'll be profitable long-term because they have real market insight from their business. A-Book protects the broker; high fees capture revenue.`,
  },
  groupCRT: {
    section: "groups",
    title: "CRT_A — Critical Full Restrict",
    short: "Most dangerous clients. Matched by: risk score ≥49, OR arbitrage detected, OR ≥3 abuse flags. Full restriction applied.",
    full: `**CRT_A (Critical Full Restrict)** — The "red alert" group.

**Who:** The most dangerous clients to the broker. These traders are highly profitable AND use abuse techniques that the broker can't compete with.

**How matched (any ONE of):**
• Risk score ≥ 49 (near-Critical level)
• Flagged as arbitrage trader
• Has 3 or more abuse flags simultaneously

**Execution:** Full A-Book with maximum restrictions.

**Actions:** Latency 150ms (slows execution), Slippage enabled, Daily Swap (no swap-free), Low leverage (1:50), Daily monitoring, Requotes on news events, NOP limit, Review for account closure.

**Why:** These clients extract significant profit through exploitative patterns. The restrictions make it harder for them to profit while protecting the broker. If restrictions don't reduce their profitability, account closure may be recommended.`,
  },
  groupSPD: {
    section: "groups",
    title: "SPD_A — Speed Risk A-Book",
    short: "Scalper/HFT/NewsTrader who is also profitable. Both conditions must be true (AND logic). A-Book with latency and slippage.",
    full: `**SPD_A (Speed Risk A-Book)** — Speed abuse + profitability.

**Who:** Clients who trade extremely fast (scalping, HFT, or news trading) AND are profitable doing it.

**How matched (ALL must be true):**
• Has speed flag (Scalper, HighFrequencyTrader, or NewsTrader)
• Is profitable (Revenue score above threshold)

**Execution:** Full A-Book with speed countermeasures.

**Actions:** Latency 150ms, Slippage enabled, Daily Swap, Reduced leverage (1:100), Daily monitoring, Requotes on news events.

**Key distinction from CRT_A:** SPD_A clients have speed-specific risk but may not have other abuse patterns. CRT_A is reserved for clients with broader, more severe abuse combinations. A fast trader who isn't profitable stays in STD_AB or STD_B.`,
  },
  groupPRO: {
    section: "groups",
    title: "PRO_A — Professional A-Book",
    short: "Profitable but clean trading strategy — no speed abuse, no compliance flags. Respected clients. A-Book with best execution.",
    full: `**PRO_A (Professional A-Book)** — Skilled, legitimate traders.

**Who:** Clients who are genuinely profitable through skill and good strategy, without any abuse patterns.

**How matched (ALL must be true):**
• Is profitable (Revenue score above threshold)
• Is clean (Compliance score below threshold — no abuse flags)
• No speed flags (not a scalper/HFT/news trader)

**Execution:** Full A-Book with premium treatment.

**Actions:** Best execution (no delay — they deserve it), 5-Day Swap Free, Standard leverage (1:200), Monthly monitoring, No promotions (they don't need incentives).

**Why respect them:** These traders profit through genuine skill — fundamental analysis, proper risk management, good timing. They're expensive for the broker but earn their profits fairly. Restricting them would be unfair and could cause them to leave.`,
  },
  groupSTDAB: {
    section: "groups",
    title: "STD_AB — Standard Hybrid",
    short: "Starting to show risk signals. Risk score ≥14 but didn't match higher groups. Hybrid A/B-Book execution.",
    full: `**STD_AB (Standard Hybrid)** — Emerging risk clients.

**Who:** Clients with some risk signals but not enough to warrant full A-Book or the specific patterns of higher-priority groups.

**How matched:** Risk score ≥ 14 (and didn't match B2B, HDG, CRT, SPD, or PRO groups)

**Execution:** Hybrid A/B-Book — a percentage of volume is A-Booked based on risk level, the rest is B-Booked.

**Actions:** Best execution, 5-Day Swap Free, Reduced leverage (1:200), Weekly monitoring, No promotions.

**The "watch zone":** These clients are not yet dangerous but are trending in that direction. Weekly monitoring catches early warning signs. If their score increases or they develop abuse flags, they'll move up to CRT_A, SPD_A, or PRO_A.`,
  },
  groupSTDB: {
    section: "groups",
    title: "STD_B — Standard B-Book",
    short: "Safe clients with minimal risk signals. The broker's profit center. Full B-Book execution with promotions eligible.",
    full: `**STD_B (Standard B-Book)** — The "default" group for safe clients.

**Who:** Clients with no significant risk signals. Low profitability, no abuse flags, normal trading patterns. The majority of clients (typically 90%+) fall here.

**How matched:** Default condition — catches everyone not matched by groups above.

**Execution:** Full B-Book — the broker takes the other side of their trades.

**Actions:** Best execution (no delay), Swap Free, Standard leverage (1:500), Promotions eligible (cashback, loyalty, rebates).

**Why B-Book:** These clients are statistically likely to lose money trading. B-Book means the broker profits from their losses. This is the broker's primary revenue source. Promotions (cashback, bonuses) encourage more trading volume, increasing broker revenue.`,
  },

  // ═══════════════════════════════════════════
  // FLAGS & ABUSE DETECTION
  // ═══════════════════════════════════════════
  flagScalper: {
    section: "flags",
    title: "Scalper Flag",
    short: "Client opens and closes trades very quickly — typically within seconds to minutes. Exploits small price movements. Detected by speed metrics and trade duration.",
    full: `**Scalper** — A trader who makes many rapid trades, holding positions for very short periods (seconds to minutes), profiting from small price movements.

**Why it's a risk:** Scalpers exploit the broker's pricing latency — they see price changes milliseconds before the broker can update quotes, locking in guaranteed small profits. At scale, this is very expensive for the broker.

**Detection:** Based on speed profiling columns:
• High PctUnder30Sec (many trades closed within 30 seconds)
• High PctUnder2Min (many trades closed within 2 minutes)
• Low AvgTradeDuration (average trade is very short)

**Scoring impact:** 3 points in Compliance (Speed <30s group). Also contributes to SPD_A group assignment when combined with profitability.

**Action:** If profitable → A-Book with latency/slippage. If unprofitable → less concern (they're losing on their own).`,
  },
  flagHedgingAbuser: {
    section: "flags",
    title: "HedgingAbuser Flag",
    short: "Client uses hedging strategies to exploit the broker — typically Friday hedge breaks (hedging before weekend, then closing one side for gap profit).",
    full: `**HedgingAbuser** — A client who uses hedging not as risk management but as an exploitation strategy.

**The Friday Hedge Break pattern:**
1. Late Friday: Open a BUY 1 lot GOLD + SELL 1 lot GOLD (hedged — no risk over weekend)
2. Weekend: A major event happens, gold gaps up $50
3. Monday: Close the profitable side (+$5,000), let the losing side run or close it later
4. The broker gave swap-free on both sides but the client extracted weekend gap profit

**Detection:** FridayHedgeBreaks column counts these instances. FridayLateOpens counts trades opened in the last session on Fridays.

**Scoring impact:** 2 points in Compliance (FridayHedgeBreaks group).

**Action:** Enable daily swap, monitor Friday trading activity, flag for manual review.`,
  },
  flagCommissioner: {
    section: "flags",
    title: "Commissioner Flag",
    short: "Client generates excessive commissions/rebates through churning — high-volume low-profit trades designed to earn commission rebates.",
    full: `**Commissioner** — A client who trades primarily to earn commission rebates rather than to profit from market movements.

**The exploitation pattern:**
1. Broker offers IB (Introducing Broker) commission rebate: $5 per lot traded
2. Client opens and quickly closes trades at breakeven or small loss
3. Each trade earns $5 rebate regardless of P&L
4. Net result: Client "profits" from rebates while the broker pays out

**Detection:** CommissionRebateExploitation column measures the dollar amount of suspected commission abuse. This is calculated by comparing commission payments against trading profitability.

**Scoring impact:** Up to 3 points in Compliance (Commission Abuse group). The Commissioner flag itself adds 2 points.

**Important note:** After system validation, the detection was refined from 29 flagged clients to only 4 — filtering out legitimate high-volume traders whose commissions were proportional to their trading.`,
  },
  flagNewsTrader: {
    section: "flags",
    title: "NewsTrader Flag",
    short: "Client concentrates trades around major news events (NFP, CPI, interest rate decisions) to exploit volatility spikes and price gaps.",
    full: `**NewsTrader** — A trader who places positions immediately before or after major economic news releases.

**Why it's a risk:** During news events (Non-Farm Payrolls, CPI, central bank decisions), prices move extremely fast. The broker's pricing systems lag behind real prices. News traders exploit this latency to enter positions at stale prices before the new price feeds in.

**Detection:** TradesNearNews10Min counts trades opened within 10 minutes of scheduled news events. The SQL pipeline maps trade timestamps against an economic calendar.

**Scoring impact:** Up to 3 points in Compliance (News Trading group) for >30 news trades. The flag itself adds 1 point.

**Action:** Requotes on news events (reject orders during high-volatility windows), widen spreads around news, latency delays.`,
  },
  flagSwaper: {
    section: "flags",
    title: "Swaper Flag",
    short: "Client exploits swap-free accounts — holds positions specifically to profit from swap differentials that they should be paying but aren't.",
    full: `**Swaper** — A client exploiting swap-free (Islamic) account privileges.

**The exploitation:**
Swap = overnight interest charged for holding positions. Islamic accounts are swap-free for religious reasons. Some traders:
1. Open a swap-free account
2. Hold large positions for days/weeks specifically in instruments with high positive swap
3. On a normal account they'd earn swap but also pay on the opposite side; on swap-free, they avoid all costs
4. Result: profit from the position itself while dodging the carry cost

**Detection:** |SwapInOut| measures the absolute value of swap amounts. Large values indicate the client is benefiting from (or avoiding) significant swap charges.

**Scoring impact:** Up to 3 points in Compliance (Swap Abuse group). The Swaper flag adds 2 points.

**Action:** Remove swap-free privilege, enable daily swap charges, or apply admin fees.`,
  },
  flagHeavyTrader: {
    section: "flags",
    title: "HeavyTrader Flag",
    short: "Client trades very large volumes — high daily lot volume that creates significant broker exposure.",
    full: `**HeavyTrader** — A client who trades unusually large volumes.

**Detection:** Based on DailyVolumeLots exceeding thresholds. A "heavy" trader might trade 10+ lots per day consistently.

**Scoring impact:** 3 points in Strategy category. Combined with DailyVolumeLots tiered scoring (up to 3 more points in the same group).

**Risk:** Large volumes amplify all other risks. A profitable heavy trader costs much more than a profitable small trader. Combined with any abuse pattern, the financial impact is multiplied.

**Action:** Depends on other factors. If clean (no abuse flags) → may be a VIP worth keeping. If combined with abuse flags → priority for A-Book and restrictions.`,
  },
  flagHFT: {
    section: "flags",
    title: "HighFrequencyTrader (HFT) Flag",
    short: "Client executes an extremely high number of trades per day — algorithmic or automated trading suspected.",
    full: `**HighFrequencyTrader** — A client executing an unusually high number of trades daily.

**Detection:** Based on TradingFreqPerDay exceeding thresholds. HFT clients might execute 50-100+ trades per day.

**Scoring impact:** 3 points in Strategy category. Combined with TradingFreqPerDay tiered scoring (up to 3 more points).

**Distinction from Scalper:** A Scalper is defined by trade DURATION (how fast trades close). An HFT is defined by trade COUNT (how many trades per day). A client can be both — many fast trades — or just one (many trades that last hours, or few trades that close in seconds).

**Action:** A-Book if profitable. Investigate whether an EA (Expert Advisor / robot) is being used. HFT combined with high win rate strongly suggests automation.`,
  },
  flagIPAbuser: {
    section: "flags",
    title: "IP_Abuser Flag",
    short: "Client shares IP addresses with other trading accounts — suggests coordinated trading, copy trading, or multiple account fraud.",
    full: `**IP_Abuser** — A client whose trading IP address is shared with other accounts.

**Detection:** IP_ODT (Other Distinct Traders) counts how many OTHER trading accounts have used the same IP addresses as this client. Server IPs (used by VPS hosting, EA providers) are excluded from detection to prevent false positives.

**Why it's a risk:**
• Multiple accounts under one person → evading position limits or bonus rules
• Copy trading rings → coordinated exploitation
• Account management → one person trading others' accounts (regulatory issue)

**Scoring impact:** Up to 3 points in Compliance (IP Abuse group). The flag itself adds 1 point.

**Note:** Some IP sharing is legitimate (shared office, family, VPN). The system counts severity by IP_ODT number — sharing with 1-2 accounts is flagged lower than sharing with 10+.`,
  },
  flagHedger: {
    section: "flags",
    title: "Hedger Flag",
    short: "Client regularly uses hedging (opening opposing positions on the same instrument). Not necessarily abusive — could be legitimate risk management.",
    full: `**Hedger** — A client who frequently opens opposing positions (buy and sell) on the same instrument.

**Detection:** Based on HedgeRatio (proportion of hedged volume to total volume) and HedgeVolumeLots.

**Scoring impact:** 1 point in Strategy category. This is intentionally low because hedging itself isn't abuse — it's HOW and WHEN they hedge that matters.

**Important distinction:**
• **Legitimate hedging** — Client has real exposure (e.g., gold dealer hedging inventory). Hedger flag + ClientType "hedger" → HDG_A group.
• **Abusive hedging** — Friday hedge breaks, swap exploitation. HedgingAbuser flag is separate and scores higher in Compliance.

A client with only the Hedger flag is usually not concerning. It becomes concerning combined with: HedgingAbuser, short hedge durations, Friday patterns, or very high hedge ratios (>0.7).`,
  },
  flagLongTrader: {
    section: "flags",
    title: "LongTrader Flag",
    short: "Client has extreme directional bias — heavily favoring long (buy) positions. Could indicate market view or manipulation pattern.",
    full: `**LongTrader** — A client with strong directional bias toward long (buy) positions.

**Detection:** Based on LongPct — the percentage of trades that are buy positions. Extreme values (>80% or <20%) trigger the flag and additional scoring.

**Scoring impact:** 1 point in Strategy category (base flag). LongPct extreme scoring adds up to 3 more points for >95% directional bias.

**Why it matters:** Extreme directional bias can indicate:
• Strong conviction in one direction (legitimate but risky for broker if correct)
• Gold/oil buyers who always go long (common in Middle East markets)
• Possible insider information or coordinated trading

**By itself, this flag is low concern.** It becomes relevant when combined with profitability — a heavily directional trader who is also very profitable may have an information advantage.`,
  },
  flagGainerTrader: {
    section: "flags",
    title: "GainerTrader Flag",
    short: "Client consistently extracts profit from the broker — a net 'gainer' over time. This is the most impactful revenue risk flag.",
    full: `**GainerTrader** — A client who has established a consistent pattern of extracting profit.

**Detection:** Based on overall profitability patterns across multiple metrics — ProfitFactor, net Profit, win rate.

**Scoring impact:** 7 points in Revenue category — the single highest-scoring individual factor. This reflects that a proven gainer is the most direct and predictable revenue risk.

**Why it's important:** Many flags detect HOW a client might be risky. GainerTrader confirms they ARE extracting money. A client with ProfitFactor 3.0 might be lucky; a GainerTrader flag means the pattern is established.

**Also used in:** Arbitrage detection conditions. GainerTrader + Scalper + high win rate + fast execution is a strong arbitrage signal.`,
  },

  // ═══════════════════════════════════════════
  // FINANCIAL METRICS
  // ═══════════════════════════════════════════
  metricVolume: {
    section: "financial",
    title: "Volume (Lots)",
    short: "Total trading volume in lots. 1 standard lot = 100,000 units of base currency. Higher volume = more broker exposure and commission revenue.",
    full: `**Volume** measures total trading activity in lots.

**What is a lot?** 1 standard lot = 100,000 units of the base currency. For GOLD, 1 lot = 100 ounces. The actual dollar exposure depends on the instrument.

**In the Dashboard:** The Volume card shows filtered volume (current view) and total volume (all data). The percentage shows what fraction of total volume is in the current filter. MoM% compares to the previous month.

**Why it matters:** Volume is the broker's primary revenue driver through spreads and commissions. High-volume clients are valuable BUT also create more exposure. A high-volume client who is also profitable is the most expensive client.

**Formatting:** Displayed as lots (K = thousands, M = millions of lots).`,
  },
  metricProfit: {
    section: "financial",
    title: "Profit (Net P&L)",
    short: "Client's net profit/loss. Positive = client is making money (broker is losing). Negative = client is losing (broker is profiting in B-Book).",
    full: `**Profit** is the client's net P&L (Profit and Loss) across all closed trades.

**Positive Profit** = Client is making money. This is RISK for the broker because:
• In B-Book: broker directly loses what the client gains
• In A-Book: broker doesn't lose, but also doesn't profit from the client's activity

**Negative Profit** = Client is losing money. This is REVENUE for the broker in B-Book execution.

**In the Dashboard:** Total Profit card shows the sum. A red "Total: -$39.8M" means clients collectively lost $39.8M — that's broker revenue in B-Book.

**GrossProfit vs Net Profit:** GrossProfit is the sum of all winning trades (always positive). Net Profit subtracts all losing trades. A client might have GrossProfit of $50K but Net Profit of -$10K if their losses exceeded their wins.`,
  },
  metricEquity: {
    section: "financial",
    title: "Equity",
    short: "Client's account value = Balance + FloatingPL. Represents total exposure. Changes in real-time as open positions move.",
    full: `**Equity** = Account Balance + Unrealized (Floating) P&L on open positions.

**Balance** is the settled cash — deposits minus withdrawals plus closed trade P&L.
**FloatingPL** is the unrealized gain/loss on currently open positions.

**Example:** Client deposits $10,000, has $2,000 in closed profits (Balance = $12,000), and has open positions currently up $3,000 (FloatingPL = $3,000). Equity = $15,000.

**Why it matters:** Equity determines:
• How much the client could withdraw if they closed everything
• The EquityRisk classification (Low/Moderate/High)
• Margin requirements and exposure limits

**Equity Growth** is calculated as: (Current Equity - Previous Month Equity) / Previous Month Equity × 100%. Rapid equity growth (>50%) is a risk signal.`,
  },
  metricDeposit: {
    section: "financial",
    title: "Deposit",
    short: "Total money deposited by the client. Higher deposits = more capital at risk. First Deposit (FTD) is tracked separately for client lifecycle analysis.",
    full: `**Deposit** is the total amount the client has funded their account with.

**In the Dashboard:** Deposit card shows the sum for all clients. It compares to the previous month for MoM change. High deposit growth can indicate: new client acquisition success, existing clients adding capital (good sign), or potential money laundering (regulatory concern at very high levels).

**Related metrics:**
• **First_Deposit (FTD)** — The amount of the client's very first deposit. Analyzed in the FTD Analysis section.
• **Deposit_Count** — How many deposits the client has made.
• **Time to First Deposit** — Days between registration and first deposit.`,
  },
  metricWithdraw: {
    section: "financial",
    title: "Withdraw",
    short: "Total money withdrawn by the client. Large withdrawals after profits signal risk extraction. Withdraw/Deposit ratio indicates net capital flow.",
    full: `**Withdraw** is the total amount the client has taken out of their account.

**Why it matters for risk:**
• Large withdrawals after a profitable period = client extracting gains (confirms they're a "gainer")
• Withdraw close to Deposit total = client is leaving / closing account
• Withdraw > Deposit = client has profited and is taking out more than they put in

**In the Dashboard:** Withdraw card shows the sum. MoM increase in withdrawals can signal clients are cashing out — which might indicate loss of confidence or a coordinated exit.`,
  },
  metricCredit: {
    section: "financial",
    title: "Credit",
    short: "Bonus/credit amounts given to the client (welcome bonus, deposit match, etc.). Credits increase client trading power without real deposit.",
    full: `**Credit** represents bonus or promotional credit given to the client.

**Types of credit:** Welcome bonuses, deposit match bonuses, loyalty credits, cashback, rebates.

**Risk implications:** Credits increase the client's trading power without them depositing real money. If a client trades with $10,000 credit + $1,000 deposit and profits $5,000, the broker has lost money relative to the small deposit.

**In the Dashboard:** Credit card shows total credits outstanding. High credit with low deposit ratio is a concern. Credits are typically only given to STD_B (safe) clients — risky clients should NOT receive promotions.`,
  },
  metricMoM: {
    section: "financial",
    title: "Month-over-Month (MoM) Change",
    short: "Percentage change compared to the previous month. Green ▲ = increase, Red ▼ = decrease. Shown on Dashboard cards for all key metrics.",
    full: `**MoM (Month-over-Month)** percentage shows how a metric has changed compared to the previous month.

**Calculation:** ((Current Month Value - Previous Month Value) / Previous Month Value) × 100%

**Color coding:**
• For Volume, Deposit: Green ▲ = growth (good for business)
• For Behavioral High Risk, Equity High: Green ▼ = decrease (risk reducing, good), Red ▲ = increase (risk growing, bad)
• For Withdraw: context-dependent

**Previous month** is determined per-company — each company uses its own prior month's data for comparison, so metrics are never mixed between companies.`,
  },

  // ═══════════════════════════════════════════
  // SPEED PROFILING
  // ═══════════════════════════════════════════
  speedOverview: {
    section: "speed",
    title: "Speed Profiling System",
    short: "Analyzes how fast clients trade using 3 layers (under 30s, under 2min, under 5min), plus consistency analysis and win rate comparison.",
    full: `Speed Profiling detects clients who trade abnormally fast — a key indicator of scalping, algorithmic trading, or latency arbitrage.

**3 Speed Layers:**
• **Layer 1: PctUnder30Sec** — Percentage of trades closed within 30 seconds. This is extreme speed — almost certainly automated.
• **Layer 2: PctUnder2Min** — Percentage of trades closed within 2 minutes. Fast trading that could be manual scalping or EA.
• **Layer 3: PctUnder5Min** — Percentage of trades closed within 5 minutes. Broader speed signal.

Each layer scores independently in the Compliance risk category. A client who is fast at ALL layers accumulates points from all three.

**Additional metrics:**
• **SpeedConsistency** — Standard deviation of trade durations. Low values (<0.15) mean trades are unnaturally uniform (bot-like). Human traders have variable timing.
• **FastTradeWinRate** — Win percentage on trades closed within 2 minutes. High fast win rates suggest the client has an execution advantage.
• **SlowTradeWinRate** — Win percentage on trades held longer than 2 minutes. Comparing fast vs slow win rates reveals if speed is their edge.`,
  },
  speedTiers: {
    section: "speed",
    title: "Speed Tiers (Normal → Extreme)",
    short: "5 tiers classify speed behavior: Normal, Light, Scalper, Heavy, Extreme. Based on percentage of fast trades and consistency.",
    full: `The Speed Profiling tab in Risk Monitor classifies clients into 5 tiers:

**Normal** 🟢 — No significant fast trading. Most trades take reasonable time.

**Light** 🔵 — Some fast trades but within acceptable limits.
• >15% under 2 minutes OR >40% under 5 minutes

**Scalper** 🟡 — Clear scalping pattern.
• >10% under 30 seconds OR >30% under 2 minutes OR >60% under 5 minutes

**Heavy** 🟠 — Serious speed abuse.
• >20% under 30 seconds OR >50% under 2 minutes

**Extreme** 🔴 — Maximum speed risk, almost certainly automated.
• >50% under 30 seconds OR (>30% under 30 seconds AND SpeedConsistency < 0.15)

The tier classification is used for visualization in the Speed Profiling tab. The actual risk score impact comes from the granular factor scoring in Compliance.`,
  },
  speedConsistency: {
    section: "speed",
    title: "Speed Consistency",
    short: "Measures how uniform a client's trade durations are. Low values (< 0.15) = robotic/algorithmic. High values (> 0.5) = human/natural variation.",
    full: `**SpeedConsistency** is a normalized measure (0 to 1) of how variable a client's trade durations are.

**Low consistency (< 0.15)** = Very uniform trade durations. If a client's trades consistently last 23-27 seconds, the consistency is near zero. This is strong evidence of an automated trading system (EA/bot) because humans can't be this consistent.

**Medium consistency (0.15-0.5)** = Some uniformity. Could be semi-automated (EA with human oversight) or a very disciplined manual trader.

**High consistency (> 0.5)** = Natural variation. Trades range from seconds to hours. This is typical human behavior — sometimes trading quickly on impulse, sometimes holding longer.

**Scoring impact:** SpeedConsistency < 0.15 scores 3 points, < 0.25 scores 2 points, < 0.4 scores 1 point in Compliance.

**In charts:** The Speed Consistency Distribution chart in Risk Monitor shows the distribution across all clients, color-coded red (bot-like) to green (natural).`,
  },

  // ═══════════════════════════════════════════
  // ARBITRAGE
  // ═══════════════════════════════════════════
  arbOverview: {
    section: "arbitrage",
    title: "Arbitrage Detection",
    short: "Identifies clients suspected of arbitrage — exploiting price differences or latency to guarantee profits. Uses multiple conditions that must ALL be true.",
    full: `Arbitrage detection identifies clients who exploit pricing inefficiencies between the broker's quotes and real market prices. This is the most expensive form of client abuse.

**How it works:**
The system checks multiple conditions simultaneously. ALL ENABLED conditions must be true to flag a client as suspected arbitrage:

• ProfitFactor ≥ 2.5 (extremely profitable)
• Avg Trade Duration < 120 seconds (very fast)
• Win Rate ≥ 70% (almost always wins)
• Scalper flag (fast entry/exit)
• GainerTrader flag (proven profit extractor)

Additional conditions can be enabled: Trading frequency, daily volume, trade size, HFT flag, NewsTrader flag.

**Why ALL conditions:** Arbitrage produces a very specific signature — high profit, fast trades, high win rate, consistent patterns. Meeting just one condition doesn't indicate arbitrage; meeting all simultaneously is very strong evidence.

**Financial Impact:** The Arbitrage tab shows total profit extracted by suspected arbitrage clients and what percentage of total client profit they represent. Often a tiny number of clients account for a disproportionate share of losses.`,
  },
  arbNearMiss: {
    section: "arbitrage",
    title: "Near-Miss Clients",
    short: "Clients who match ALL BUT ONE arbitrage condition. These are on the edge of being flagged — watchlist candidates.",
    full: `**Near-Miss Clients** are those who satisfy N-1 out of N enabled arbitrage conditions.

**Example:** If 5 conditions are enabled and a client matches 4 of them, they're a near-miss. The system shows WHICH specific condition they're missing.

**Why this matters:**
• They may actually be doing arbitrage but haven't crossed one threshold yet
• They might cross the threshold next month (trending toward arbitrage)
• The missing condition might be due to data timing (e.g., ProfitFactor is 2.4, just under the 2.5 threshold)

**Action:** Near-miss clients should be added to the Monitoring watchlist for weekly review. If the missing condition is close to being met, consider proactive restrictions.

**Display:** The Arbitrage tab shows near-miss clients with their matched conditions and the specific missing condition highlighted in red.`,
  },

  // ═══════════════════════════════════════════
  // FTD ANALYSIS
  // ═══════════════════════════════════════════
  ftdOverview: {
    section: "ftd",
    title: "FTD (First Deposit) Analysis",
    short: "Analyzes first deposit patterns — average FTD by country, time-to-first-deposit, deposit frequency, and FTD vs lifetime value correlation.",
    full: `FTD (First Time Deposit) Analysis examines client acquisition and deposit patterns through 4 tabs:

**By Country** — Average first deposit amount by client country. Shows which markets bring higher-value clients. Includes total deposits, volume, and trade counts per country.

**Timing** — Time-to-first-deposit distribution: how long after registration does the client make their first deposit? Categories from "Same Day" to "6-12 Months". Faster deposits generally indicate higher intent.

**Frequency** — Deposit count distribution: how many deposits do clients make? One-time depositors vs frequent depositors. More deposits indicates engagement and retention.

**Correlation** — Relationship between first deposit size and total trading volume/trades. Does a larger FTD predict higher lifetime activity?

**Summary cards:** Average FTD, Median FTD, Average Time to First Deposit (days), Average Deposits per Client.`,
  },

  // ═══════════════════════════════════════════
  // COVERING GOLD
  // ═══════════════════════════════════════════
  coveringOverview: {
    section: "covering",
    title: "Covering Gold Concept",
    short: "Tracks how the broker hedges (covers) its gold exposure with liquidity providers. Monitors P&L, coverage ratios, and hedge effectiveness.",
    full: `**Covering Gold** analyzes the broker's hedging activity on gold (XAUUSD) trades.

**What is "covering"?**
When a client buys gold, the broker can:
1. **B-Book** — Take the other side (broker sells). If gold goes up, broker loses.
2. **A-Book / Cover** — Hedge with a liquidity provider. Broker is protected but earns only spread.

This page tracks WHEN and HOW WELL the broker covers gold positions.

**Key concepts:**
• **Net P&L** — Profit/loss from covering activity. Positive = good hedging, Negative = poor timing.
• **Coverage Ratio** — What percentage of client gold exposure is being hedged.
• **Heatmap** — Visual calendar showing daily covering P&L (green = profit, red = loss).
• **Best Covering** — Which clients' gold positions were most profitably covered.
• **Exclude List** — Clients excluded from covering analysis (e.g., B2B clients handled differently).

**Data source:** Requires a separate "Covering Gold" data upload with trade-level covering records.`,
  },

  // ═══════════════════════════════════════════
  // PAGES & NAVIGATION
  // ═══════════════════════════════════════════
  pageDashboard: {
    section: "pages",
    title: "Dashboard Page",
    short: "System overview: client counts, risk distribution, group distribution, financial summary, country analysis, alerts, DNA patterns, and FTD analysis.",
    full: `The Dashboard is the system's home page — a comprehensive overview of the entire client portfolio.

**Row 1:** Total Clients (CU) with Active/Dormant/Not Active breakdown, Risk Distribution (score-based 5 levels), Risk Overview (behavioral + equity).

**Group Distribution:** Bar showing how clients are distributed across the 7 groups. Click any group name to see its description, rules, and recommended actions.

**Row 2:** 6 Financial Cards (Volume, Profit, Equity, Deposit, Withdraw, Credit) each with filtered value, total, MoM change.

**Row 3:** Company Overview (when viewing "All"), VIP clients, Equity analysis, Profit/Loss distribution.

**Row 4:** Country Analysis map/table, Top Countries by volume/clients.

**Row 5:** Alerts (new flags, risk changes) and Client DNA (distribution of key patterns).

**Row 6:** FTD Analysis (4 tabs: Country, Timing, Frequency, Correlation).`,
  },
  pageProfiles: {
    section: "pages",
    title: "Client Profiles Page",
    short: "Searchable client list with filters. Click a client to see full detail: identity, financials, risk breakdown, flags, scoring details, and history.",
    full: `Client Profiles is the primary client lookup and investigation page.

**Filters:** Status (Active/Not Active/Dormant), B.Risk (Normal/High), E.Risk (Low/Moderate/High), Account Type, Trader Type, Account Manager. All filters combine with AND logic.

**Table columns:** Login, Name, Status, Type (account type), B.Risk, E.Risk, Risk Score (with HealthBar), Group (clickable badge), Equity, Volume.

**Client Detail Panel** (click any row):
• Identity: Login, Name, Phone, Country, Registration date, Account Manager
• Financial: Equity, Volume, Deposit, Withdraw, Profit, Credit
• Risk Breakdown: 4-category chart showing Revenue/Compliance/Strategy/Position scores with factor details
• Flags: All active flags with descriptions
• History: Month-by-month changes in risk score and group
• Scoring Detail: Every factor that scored points, organized by category and group`,
  },
  pageRiskMonitor: {
    section: "pages",
    title: "Risk Monitor Page",
    short: "Deep dive into risk patterns. 6 tabs: Overview, Flags, Timeline, Group Changes, Speed Profiling, Arbitrage Analysis.",
    full: `Risk Monitor provides detailed analysis tools across 6 tabs:

**Overview** — Risk distribution charts, risk vs volume scatter plot, highest risk clients.

**Flags** — Breakdown of each abuse flag: how many clients have it, overlap analysis (which flags appear together), and flagged client lists.

**Timeline** — Monthly trends of risk scores, flag counts, and group distributions over time.

**Group Changes** — Clients whose group changed from previous month. Shows old → new group, what caused the change (score changes, new/lost flags), with drill-down to specific scoring differences.

**Speed Profiling** — 5-tier speed classification, distribution charts for all 3 speed layers, consistency analysis, fast vs slow win rate comparison, top 15 speed offenders.

**Arbitrage** — Suspected arbitrage clients with detection conditions breakdown, financial impact analysis, distribution comparisons (arb vs all clients), and near-miss watchlist.`,
  },
  pageAM: {
    section: "pages",
    title: "Account Managers Page",
    short: "Performance dashboard for account managers (AMs). Shows client counts, risk exposure, volume, and deposit metrics per AM.",
    full: `The Account Managers page shows performance metrics for each AM:

**Overview table:** AM name, total clients, active clients, high-risk clients, volume, deposit, average risk score.

**AM Detail** (click a row): Drill down into a specific AM's client portfolio — their client list, risk distribution, and top clients by volume/risk.

This page helps management identify which AMs manage the most risk and which have the healthiest client books.`,
  },
  pageMonitoring: {
    section: "pages",
    title: "Monitoring (Watchlist)",
    short: "Manually curated watchlist of clients requiring attention. Add clients from any page — they appear here for ongoing tracking.",
    full: `The Monitoring page is a personal watchlist for clients you want to track closely.

**Adding clients:** Click the monitor/watch button on any client in Client Profiles, Risk Monitor, or other pages. They'll appear in your Monitoring list.

**Filters:** Status, Group, AM, and Flag filters let you focus on specific segments of your watchlist.

**Use cases:**
• Near-miss arbitrage clients added for weekly review
• Clients who recently changed groups (watching for further changes)
• High-equity clients with new flags
• VIP clients requiring special attention

The watchlist is stored locally and persists across sessions.`,
  },
  pageAdmin: {
    section: "pages",
    title: "Admin Panel",
    short: "System configuration: user management, risk scoring settings, group rules, arbitrage conditions, data backup/restore.",
    full: `The Admin panel provides system configuration:

**Risk Settings** — Adjust scoring factor weights, category maximums, risk level boundaries.

**Group Settings** — Configure group rules, priority order, conditions. Add/remove groups. Visual rule builder with AND/OR logic.

**Arbitrage Settings** — Enable/disable detection conditions, adjust thresholds.

**Data Management** — Backup (exports all data to Excel) and Restore (imports from backup file). Essential for disaster recovery.

**User Management** — Role-based access (Admin, Manager, Agent) for the CRM system.`,
  },

  // ═══════════════════════════════════════════
  // GLOSSARY
  // ═══════════════════════════════════════════
  glossABook: {
    section: "glossary",
    title: "A-Book",
    short: "Execution model where the broker passes client trades to a liquidity provider. Broker earns from spread/commission only, not from client losses.",
    full: `**A-Book** execution means the broker hedges the client's trade with a liquidity provider (LP). The broker doesn't take the opposite position.

• Broker profit: Spread markup + commission only
• Broker risk: Minimal (LP takes the market risk)
• Used for: Risky/profitable clients where B-Book would cause losses

Example: Client buys 1 lot EURUSD → Broker simultaneously buys 1 lot from LP → Broker earns only the spread difference (e.g., 0.5 pip = $5 per lot).`,
  },
  glossBBook: {
    section: "glossary",
    title: "B-Book",
    short: "Execution model where the broker takes the other side of client trades. Broker profits when clients lose. Used for safe/unprofitable clients.",
    full: `**B-Book** execution means the broker takes the opposite side of the client's trade internally. No external hedging.

• Broker profit: Client's full loss + spread
• Broker risk: If client profits, broker loses that amount
• Used for: Safe clients who are statistically likely to lose

Example: Client buys 1 lot EURUSD → Broker internally sells → If EURUSD drops and client loses $500, broker profits $500 + spread.

~90% of retail forex clients lose money, making B-Book the primary revenue model for brokers. The risk management system identifies which clients should NOT be B-Booked.`,
  },
  glossProfitFactor: {
    section: "glossary",
    title: "ProfitFactor",
    short: "GrossProfit ÷ |GrossLoss|. Values above 1.0 = profitable. Above 2.0 = very profitable. Above 3.0 = exceptional (potential abuse).",
    full: `**ProfitFactor** = Gross Profit ÷ |Gross Loss|

• PF < 1.0 → Client loses more than they win (broker profits in B-Book)
• PF = 1.0 → Breakeven
• PF 1.0-1.5 → Mildly profitable (manageable)
• PF 1.5-2.5 → Significantly profitable (consider A-Book)
• PF > 2.5 → Extremely profitable (definite A-Book + investigation)

This is one of the most important single metrics for risk assessment. A consistently high PF across months is a stronger signal than a one-month spike.`,
  },
  glossNOP: {
    section: "glossary",
    title: "NOP (Net Open Position)",
    short: "Net Open Position — the total unhedged exposure in one direction. NOP limits cap how much directional risk a client can hold.",
    full: `**NOP (Net Open Position)** is the net directional exposure after offsetting hedged positions.

Example: Client has BUY 5 lots GOLD + SELL 3 lots GOLD → NOP = 2 lots LONG GOLD.

NOP limits restrict the maximum net exposure a client can hold. This is applied to higher-risk groups (CRT_A, HDG_A, B2B_A) to cap the broker's maximum loss from any single client.`,
  },
  glossEA: {
    section: "glossary",
    title: "EA (Expert Advisor)",
    short: "Automated trading robot that executes trades on behalf of the client. EAs can be benign (simple rules) or sophisticated (HFT, arbitrage).",
    full: `**EA (Expert Advisor)** is MT5's term for an automated trading program (bot/robot).

EAs range from simple (e.g., "buy when moving average crosses") to highly sophisticated (latency arbitrage, statistical arbitrage, news-driven algorithms).

**How we detect EAs:** SpeedConsistency < 0.15, very high TradingFreqPerDay, uniform trade sizes, consistent execution timing. No single metric confirms EA usage, but the combination is strong evidence.`,
  },
  glossFTD: {
    section: "glossary",
    title: "FTD (First Time Deposit)",
    short: "The amount of a client's very first deposit. An important client lifecycle metric — higher FTDs often correlate with higher lifetime value.",
    full: `**FTD (First Time Deposit)** is the amount of money a client deposited when first funding their account.

FTD is a key client acquisition metric:
• High FTD → Client likely has trading experience and serious intent
• Low FTD → Could be testing the platform or a novice
• FTD by country → Shows which markets bring higher-value clients
• Time to FTD → How quickly after registration the client funds their account (faster = more engaged)`,
  },
  glossAM: {
    section: "glossary",
    title: "A.M (Account Manager)",
    short: "The sales/support person assigned to manage a client's relationship with the broker. AMs are responsible for client retention and service.",
    full: `**A.M (Account Manager)** is the broker employee assigned to a client. They handle:
• Client onboarding and KYC
• Ongoing relationship management
• Deposit encouragement and retention
• First point of contact for client issues

The system tracks which AM manages each client and aggregates performance metrics (total clients, active count, volume, risk exposure) in the Account Managers page.`,
  },
  glossSwapFree: {
    section: "glossary",
    title: "Swap-Free Account",
    short: "Account that doesn't charge overnight swap (interest). Originally for Islamic finance compliance but exploitable by swap arbitrage traders.",
    full: `**Swap-Free** accounts don't charge or pay overnight swap (rollover interest) when positions are held past the daily cutoff.

**Purpose:** Islamic finance (Sharia law) prohibits earning or paying interest. Swap-free accounts comply with this requirement.

**Exploitation risk:** Some traders open swap-free accounts specifically to avoid swap costs on carry trades, not for religious reasons. The system detects this through the Swaper flag and |SwapInOut| metrics.`,
  },
  glossLatency: {
    section: "glossary",
    title: "Latency (Execution Delay)",
    short: "Artificial delay added to trade execution for risky clients. 150ms delay makes it harder to exploit momentary price inefficiencies.",
    full: `**Latency** is an intentional delay added between when a client sends a trade order and when it's executed.

**Why it's used:** Arbitrage and scalping strategies rely on millisecond-level execution to capture small price differences. Adding a 150ms delay means the market price may move in that time, eliminating the edge.

**Applied to:** CRT_A (Critical), SPD_A (Speed Risk) groups. Standard/safe clients get best execution (no delay).

**150ms** is the standard setting — long enough to disrupt fast strategies but short enough that normal traders don't notice.`,
  },

  // ═══════════════════════════════════════════
  // CLIENT PROFILE FIELDS
  // ═══════════════════════════════════════════

  // ── Trading Behavior ──
  fMostInstrument: { section: "fields", title: "Most Instrument",
    short: "The specific instrument (e.g., GOLD, EURUSD) the client trades the most by volume.",
  },
  fInstrumentCategory: { section: "fields", title: "Instrument Category",
    short: "The asset class the client trades most: METALS, FOREX, INDICES, CRYPTO, OIL, etc. Metals and Indices carry higher strategy risk scoring.",
  },
  fTradeSize: { section: "fields", title: "Trade Size (MostUsedTradeSize)",
    short: "The lot size the client uses most frequently. 0.01 = micro lot, 0.1 = mini lot, 1.0 = standard lot (100K units).",
    example: "0.01 lots = small retail trader, 1.0+ lots = significant size, 5.0+ lots = heavy trader",
  },
  fMostActiveDay: { section: "fields", title: "Most Active Day",
    short: "The day of the week when the client places the most trades. Friday activity is particularly monitored for gap trading abuse.",
  },
  fDailyVolume: { section: "fields", title: "Daily Volume (lots)",
    short: "Average volume traded per day in lots. Calculated as: Total Volume ÷ Number of Active Trading Days. Used in Strategy risk scoring.",
    example: "0.5 lots/day = light trader, 5 lots/day = active, 12+ lots/day = heavy (triggers scoring)",
  },
  fTradesPerDay: { section: "fields", title: "Trades / Day (TradingFreqPerDay)",
    short: "Average number of trades executed per day. High frequency (50+/day) indicates possible algorithmic trading. Used in HFT detection.",
    example: "4 trades/day = normal, 25+ = active, 50+ = high frequency, 80+ = extreme (likely EA/bot)",
  },
  fTotalDeals: { section: "fields", title: "Total Deals",
    short: "Total number of deals (individual executions). One trade may consist of multiple deals if partially filled or partially closed.",
  },
  fTotalTrades: { section: "fields", title: "Total Trades",
    short: "Total number of completed round-trip trades (open → close). Each trade is one position that was opened and then closed.",
  },
  fOpenPositions: { section: "fields", title: "Open Positions (StillOpenPositions)",
    short: "Number of currently open trades that haven't been closed yet. High count (20+) increases Position Risk scoring.",
  },
  fOpenVolume: { section: "fields", title: "Open Volume (StillOpenVolumeLots)",
    short: "Total volume in lots of currently open positions. Represents the client's active market exposure right now.",
  },
  fHeldGT10: { section: "fields", title: "Held > 10 Days",
    short: "Number of trades held open for more than 10 days. Long-duration trades could indicate swing trading, carry trading, or forgotten positions.",
  },
  fVolHeldGT10: { section: "fields", title: "Vol Held > 10 Days (lots)",
    short: "Total volume of positions held open longer than 10 days. Large volume held long-term increases broker exposure.",
  },
  fAvgDuration: { section: "fields", title: "Avg Trade Duration",
    short: "Average time a trade stays open, from entry to exit. Displayed in hours. Very short durations (<5 min) suggest scalping; very long (days) suggest swing/carry trading.",
    formula: "Sum of all trade durations ÷ Number of trades",
    example: "0.5h = 30 minutes average, 5h = moderate, 150h = holding trades for days",
  },
  fAvgCloseLoss: { section: "fields", title: "Avg Close Loss Time",
    short: "Average time to close LOSING trades (in hours). If losses are closed much slower than profits, the trader may be holding losers hoping for recovery.",
  },
  fAvgCloseProfit: { section: "fields", title: "Avg Close Profit Time",
    short: "Average time to close WINNING trades (in hours). If profits are closed much faster than losses, suggests the trader cuts winners short.",
  },
  fNetProfit: { section: "fields", title: "Net Profit (TotalNetProfit)",
    short: "Total profit or loss from all closed trades. Positive = client is making money (risk to broker). Negative = client is losing (broker revenue in B-Book).",
    formula: "Gross Profit + Gross Loss (where Gross Loss is negative)",
  },
  fGrossProfit: { section: "fields", title: "Gross Profit",
    short: "Sum of all winning trades (always positive). Does NOT subtract losses. Used with Gross Loss to calculate ProfitFactor.",
  },
  fGrossLoss: { section: "fields", title: "Gross Loss",
    short: "Sum of all losing trades (always negative). The absolute amount the client lost on losing trades. Gross Profit + Gross Loss = Net Profit.",
  },
  fBestTrade: { section: "fields", title: "Best Trade",
    short: "The single largest winning trade. Large best trades (>$5K) score in Revenue Risk. May indicate luck or genuine skill.",
  },
  fWorstTrade: { section: "fields", title: "Worst Trade",
    short: "The single largest losing trade (negative value). Shows the client's maximum single-trade loss tolerance.",
  },
  fProfitFactor: { section: "fields", title: "Profit Factor",
    short: "Gross Profit ÷ |Gross Loss|. Above 1.0 = profitable. Above 2.5 = extremely profitable (high risk). One of the most important risk metrics.",
    formula: "ProfitFactor = Gross Profit ÷ |Gross Loss|",
    example: "PF 0.5 = loses twice what they win. PF 1.0 = breakeven. PF 2.0 = wins twice what they lose. PF 3.93 = very high (like this client).",
  },
  fLongPct: { section: "fields", title: "Long % (LongPct)",
    short: "Percentage of trades that are BUY (long) positions. 50% = balanced. Extreme values (>90% or <10%) suggest strong directional bias and score in Strategy Risk.",
    example: "65% = slight long bias (normal). 95% = almost all buys (scored as extreme).",
  },
  fAvgProfitTrade: { section: "fields", title: "Avg Profit Trade",
    short: "Average profit per winning trade. Used to calculate Payoff Ratio (AvgProfitTrade ÷ |AvgLossTrade|). High values score in Revenue Risk.",
  },
  fAvgLossTrade: { section: "fields", title: "Avg Loss Trade",
    short: "Average loss per losing trade (negative value). Combined with Avg Profit Trade to assess the client's risk/reward profile.",
  },
  fMaxConsecWins: { section: "fields", title: "Max Consecutive Wins",
    short: "Longest winning streak. 10+ is notable (orange), 15+ is concerning (scored in Compliance), 25+ is strong bot indicator (high score).",
    example: "5 = normal. 13 = this client (moderate). 25+ = almost certainly automated.",
  },
  fExpectedPayoff: { section: "fields", title: "Expected Payoff",
    short: "Average P&L per trade. Calculated as Net Profit ÷ Total Trades. Positive = on average each trade is profitable.",
    formula: "Net Profit ÷ Total Trades",
  },
  fWinTrades: { section: "fields", title: "Win Trades (ProfitPct)",
    short: "Number and percentage of winning trades. Win rates above 65% score in Revenue Risk. Above 75% is high (5pts). Above 70% can trigger arbitrage detection.",
  },
  fLossTrades: { section: "fields", title: "Loss Trades (LossPct)",
    short: "Number and percentage of losing trades. LossPct = 100% - ProfitPct. High loss rates are actually good for the broker (B-Book revenue).",
  },
  fSwapInOut: { section: "fields", title: "Swap In/Out",
    short: "Net swap charges or credits. The difference between swap paid and swap earned. Large absolute values (|SwapInOut| > $1K) trigger Compliance scoring.",
    formula: "|SwapInOut| is used for scoring: >$10K (3pts), >$5K (2pts), >$1K (1pt)",
  },
  fActualSwap: { section: "fields", title: "Actual Swap",
    short: "The actual swap amount charged/credited to the client's account. May differ from calculated swap if the account is swap-free.",
  },

  // ── Abuse Detection ──
  fBehavioralRisk: { section: "fields", title: "Behavioral Risk (B.Risk)",
    short: "Binary: Normal or High. Based on trading behavior flags (scalping, hedging abuse, commission exploitation, etc.). High = dangerous trading patterns detected.",
  },
  fEquityRisk: { section: "fields", title: "Equity Risk (E.Risk)",
    short: "Three levels: Low, Moderate, High. Based on the client's equity size, equity growth rate, and open position exposure. High = significant financial exposure.",
  },
  fBrokerRiskScore: { section: "fields", title: "Broker Risk Score",
    short: "Combined 0-100 score from 113 factors across 4 categories. Compliance (35pts) + Revenue (30pts) + Strategy (25pts) + Position (10pts). Higher = more risk to broker.",
    formula: "Score = Compliance (max 35) + Revenue (max 30) + Strategy (max 25) + Position (max 10)",
    example: "Score 47 = High risk. Breakdown: Compliance=12, Revenue=23, Strategy=7, Position=5",
  },
  fClientProfiling: { section: "fields", title: "Client Profiling (Flags)",
    short: "Comma-separated list of detected trading behavior flags: GainerTrader, Scalper, Hedger, HedgingAbuser, Commissioner, NewsTrader, etc.",
  },
  fSpeed30: { section: "fields", title: "Speed <30s (PctUnder30Sec)",
    short: "Percentage of trades closed within 30 seconds. This is extreme speed — Layer 1 of speed detection. >10% starts scoring, >50% is extreme (likely automated).",
    example: "0% = no ultra-fast trades (clean). 30% = concerning. 50%+ = almost certainly a bot.",
  },
  fSpeed2m: { section: "fields", title: "Speed <2min (PctUnder2Min)",
    short: "Percentage of trades closed within 2 minutes. Layer 2 of speed detection. >20% starts scoring. Also used as threshold for Fast Win Rate calculation.",
  },
  fSpeed5m: { section: "fields", title: "Speed <5min (PctUnder5Min)",
    short: "Percentage of trades closed within 5 minutes. Layer 3 (broadest) of speed detection. >40% starts scoring. Captures general scalping tendency.",
  },
  fSpeedConsistency: { section: "fields", title: "Speed Consistency",
    short: "How uniform trade durations are (0-1). Low (<0.15) = robotic/bot-like (every trade same duration). High (>0.5) = natural human variation.",
    example: "0.12 = very uniform, likely automated. 0.66 = natural variation (like this client). 0.85 = highly variable (human).",
  },
  fFastWinRate: { section: "fields", title: "Fast Win Rate (FastTradeWinRate)",
    short: "Win percentage on trades closed within 2 minutes. High fast win rate (>75%) suggests execution advantage or latency arbitrage. Scored in Compliance.",
    example: "0% = no fast trades or all fast trades lost. 85%+ = strong signal of automated profitable scalping.",
  },
  fSlowWinRate: { section: "fields", title: "Slow Win Rate (SlowTradeWinRate)",
    short: "Win percentage on trades held longer than 2 minutes. Compared with Fast Win Rate — if fast WR >> slow WR, speed is their edge (concerning).",
  },
  fHedgeRatio: { section: "fields", title: "Hedge Ratio",
    short: "Proportion of hedged volume to total volume (0-1). 0 = no hedging. 0.5 = half hedged. 1.0 = fully hedged. >0.5 triggers Strategy scoring.",
    formula: "Hedge Volume (lots) ÷ Total Volume (lots)",
    example: "0.0 = no hedging. 0.3 = some hedging. 1.0 = this client is fully hedged (all positions covered).",
  },
  fHedgeDeals: { section: "fields", title: "Hedge Deals (HedgeDealsCount)",
    short: "Number of hedging deals detected — trades that create an opposing position on the same instrument while another position is open.",
  },
  fHedgeVolume: { section: "fields", title: "Hedge Volume (lots)",
    short: "Total volume of hedge positions in lots. High hedge volume combined with short hedge duration is a stronger abuse signal.",
  },
  fAvgHedgeDuration: { section: "fields", title: "Avg Hedge Duration",
    short: "Average time hedges are held. Short hedges (<5 min) are more likely to be abusive (e.g., Friday hedge breaks). Long hedges suggest legitimate risk management.",
    example: "30s = extremely short (suspicious). 131h = this client holds hedges for days (legitimate hedger pattern).",
  },
  fFridayLateOpens: { section: "fields", title: "Friday Late Opens",
    short: "Number of trades opened in the last trading session on Fridays. Opening trades late Friday is a common setup for weekend gap exploitation.",
    example: "0 = clean. >5 = concerning (scoring starts). >10 = serious abuse signal.",
  },
  fFridayLateLots: { section: "fields", title: "Friday Late Open Lots",
    short: "Total volume of Friday late-opened trades. Large volume late on Friday amplifies the risk — bigger positions = bigger potential gap profit.",
  },
  fFridayHedgeBreaks: { section: "fields", title: "Friday Hedge Breaks",
    short: "Times a client opened a hedge on Friday then broke it (closed one side) to profit from the weekend gap. The most specific Friday abuse indicator.",
    example: "0 = clean. >3 = strong abuse signal (scoring: 2pts). >5 = serious (3pts).",
  },
  fFridayHBLots: { section: "fields", title: "Friday HB Lots",
    short: "Total volume of Friday hedge break trades. Measures the scale of Friday hedge break abuse — larger lots = bigger exploitation.",
  },
  fNewsTrades: { section: "fields", title: "News Trades (TradesNearNews10Min)",
    short: "Number of trades opened within 10 minutes of major economic news events (NFP, CPI, rate decisions). High counts suggest news-driven latency exploitation.",
    example: "2 = minimal (like this client). 10+ = scoring starts. 30+ = heavy news trading.",
  },
  fCommissionExploit: { section: "fields", title: "Commission Exploit",
    short: "Dollar amount of suspected commission rebate exploitation. Calculated by comparing commission earned against actual trading profitability. $0 = clean.",
  },
  fRemainingSwap: { section: "fields", title: "Remaining Swap",
    short: "Swap amount still pending or accrued. Shows the current swap position — relevant for swap-free account abuse detection.",
  },
  fLoginSameIP: { section: "fields", title: "Login Same IP",
    short: "Other Login numbers that share IP addresses with this client. 'None' = clean. If populated, shows which other accounts accessed from the same IPs.",
  },
  fCUSameIP: { section: "fields", title: "CU Same IP",
    short: "Other CU (client unit) accounts sharing IP addresses. More concerning than Login Same IP because it means different PEOPLE sharing IPs, not just multiple accounts of the same person.",
  },
  fIPODT: { section: "fields", title: "IP ODT (Other Distinct Traders)",
    short: "Count of other distinct trading accounts that share IP addresses with this client. 0 = clean. Higher numbers increase Compliance scoring. Server IPs are excluded.",
    example: "0 = unique IPs. 3 = shares IPs with 3 other traders. 10+ = serious (could be a trading room or copy trading ring).",
  },
  fClientGroup: { section: "fields", title: "Client Group",
    short: "The assigned risk group (B2B_A, HDG_A, CRT_A, SPD_A, PRO_A, STD_AB, STD_B). Determines execution model (A-Book/B-Book) and risk controls. Click the badge for full details.",
  },
  fArbSuspected: { section: "fields", title: "Arbitrage Suspected",
    short: "Whether the client matches ALL enabled arbitrage detection conditions. YES = suspected arbitrage trader. Automatically assigned to CRT_A group.",
  },

  // ── Account Info ──
  fCUAccount: { section: "fields", title: "CU Account",
    short: "Client Unit number — groups all trading accounts (Logins) belonging to the same person. Used for deduplication in client counts.",
  },
  fClientType: { section: "fields", title: "Client Type",
    short: "Business classification: 'Hedger' (physical hedger), 'b2b' (business-to-business), or standard. Used for group assignment — hedgers go to HDG_A, b2b to B2B_A.",
  },
  fAccountType: { section: "fields", title: "Account Type",
    short: "MT5 account configuration. Format: 'PLAN | PARAMS' (e.g., 'VAL | EXECUTIVE\\0C-20MU-30R-5SO'). The first part indicates the account plan/tier.",
  },
  fAccountManager: { section: "fields", title: "Account Manager (A.M)",
    short: "The broker's sales/support person assigned to manage this client. 'X' or blank = no manager assigned.",
  },
  fCountry: { section: "fields", title: "Country",
    short: "Client's registered country. Used in FTD Analysis by country and for regional risk patterns.",
  },
  fRegistration: { section: "fields", title: "Registration Date",
    short: "When the client first registered their account. Used with First Deposit Time to calculate Time-to-First-Deposit.",
  },
  fLastAccess: { section: "fields", title: "Last Access",
    short: "When the client last logged into their trading platform. Long gap between Last Access and today suggests dormant client.",
  },
  fCRMOpenDate: { section: "fields", title: "CRM Open Date",
    short: "When the client's CRM record was created. May differ from Registration date if CRM was set up later.",
  },
  fFirstDeposit: { section: "fields", title: "First Deposit (FTD)",
    short: "The amount of the client's very first deposit. Higher FTDs often correlate with higher lifetime value and more serious trading intent.",
  },
  fFirstDepositTime: { section: "fields", title: "First Deposit Time",
    short: "The date/time of the first deposit. Time between Registration and this date = Time-to-First-Deposit (analyzed in FTD Analysis).",
  },
  fDepositCount: { section: "fields", title: "Deposit Count",
    short: "Total number of deposits made. Multiple deposits indicate ongoing engagement. One deposit only may suggest a test client or one-time trader.",
  },
  fLifetime: { section: "fields", title: "Lifetime (days)",
    short: "Number of days since client registration. Longer lifetime with high profitability = more confirmed risk pattern (not just luck).",
  },
  fStatus: { section: "fields", title: "Client Status",
    short: "Current account status: Active (trading recently), Not Active (registered but not trading), Dormant (was active, now inactive for extended period).",
  },
  fLeverage: { section: "fields", title: "Leverage",
    short: "The maximum leverage allowed on the account (e.g., 1:500, 1:200). Lower leverage is applied to riskier groups. 0 = no active leverage setting.",
  },
  fIPAddress: { section: "fields", title: "IP Address",
    short: "The client's most recent trading IP address. Used for IP abuse detection — checking if other traders use the same IP.",
  },
  fEquity: { section: "fields", title: "Equity",
    short: "Current account value = Balance + FloatingPL. This is what the client would have if they closed all positions now.",
    formula: "Equity = Account Balance + Floating P/L (unrealized profit/loss)",
  },
  fEquityPrevDay: { section: "fields", title: "Equity Prev Day",
    short: "Equity value at the end of the previous trading day. Comparing with current equity shows daily change.",
  },
  fEquityPrevMonth: { section: "fields", title: "Equity Prev Month",
    short: "Equity value at the end of the previous month. Used to calculate Equity Growth percentage for Position Risk scoring.",
    formula: "Equity Growth = (Current Equity - Prev Month Equity) / Prev Month Equity × 100%",
  },
  fFloatingPL: { section: "fields", title: "Floating P/L",
    short: "Unrealized profit or loss on currently open positions. Green = positions are in profit (client could close and extract money). Red = positions are losing.",
  },
  fRelatedAccounts: { section: "fields", title: "Related Accounts",
    short: "Other Login numbers linked to the same CU Account (same person). Shows all trading accounts owned by this client.",
  },
  fRelatedProfit: { section: "fields", title: "Related Profit",
    short: "Combined net profit across all related accounts. Important because a client may spread risk/profits across multiple accounts.",
  },
};

// Helper: get all entries for a section
export function getHelpBySection(sectionId) {
  return Object.entries(HELP)
    .filter(([, v]) => v.section === sectionId)
    .map(([id, v]) => ({ id, ...v }));
}

// Helper: search help entries
export function searchHelp(query) {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return Object.entries(HELP)
    .filter(([, v]) => {
      return v.title.toLowerCase().includes(q) ||
        v.short.toLowerCase().includes(q) ||
        (v.full && v.full.toLowerCase().includes(q));
    })
    .map(([id, v]) => ({ id, ...v }));
}
